import { isX402BoonBackedReview, type RouteNote, type X402ReceiptEvidenceBundle, type X402Review } from "@boon/x402-route";
import { API_URL, type RoutedBoonsResponse } from "./api";

const X402_SITE_READ_PREFIX = "/api/site/x402";

export type X402ContextMode = "all" | "with" | "raw";
export type X402SortMode = "reviewed" | "recent";

export function shouldUseMergedRecentReviewFeed(query: string, sort: X402SortMode): boolean {
  return !query && sort === "recent";
}

export interface X402LinkedAccount {
  handle: string;
  provider: "github" | "x" | "agent";
  linkedWallet: string;
  profileUrl: string;
  verification: "boon_oauth_link" | "erc8004_indexed_at_recognition";
  linkedAt?: string | null;
}

export interface X402RouteContext {
  version: "boon.x402-route-context/v1";
  publisher: string;
  participantRoles: Array<"tipper" | "endpoint" | "network">;
  publishedAt: number;
  publisherAccounts: X402LinkedAccount[];
  note: RouteNote;
  offer: {
    network: string;
    asset: string;
    payTo: string;
    amountAtomic: string;
    responseDigest: string;
    responseBytes: number;
    expiresAt: number;
  };
  verification: {
    publisherSignature: "valid";
    exactNoteBytes: "valid";
    challengeBinding: "not_verified";
    endpointFetched: false;
  };
  signedPayload?: {
    noteJson: string;
    offerJson: string;
    signature: string;
  };
}

interface X402PublicReviewBase {
  evidenceKind: "boon_backed" | "receipt_verified" | "self_reported";
  routeId: string;
  reviewText: string;
  reviewer: {
    wallet: string;
    accounts: X402LinkedAccount[];
  };
  createdAt: number;
  publishedAt: number;
}

export interface X402BoonBackedPublicReview extends X402PublicReviewBase {
  version: "boon.x402-review/v1";
  evidenceKind: "boon_backed";
  recognition: {
    routeId: string;
    transactionHash: string;
    logIndex: string;
    endpointGratuityUsdc: string;
    networkGratuityUsdc: string;
    totalRecognitionUsdc: string;
    boonBurned: string;
    blockTimestamp: string;
  };
  receipt: null;
  verification: {
    reviewerSignature: "valid";
    routedRecognition: "verified";
    reviewerWasTipper: true;
    receiptPayerBinding: null;
  };
  signedPayload?: {
    review: {
      version: "1";
      routeId: string;
      recognitionTxHash: string;
      recognitionLogIndex: number;
      reviewText: string;
      createdAt: number;
    };
    signature: string;
  };
}

export interface X402ReceiptPublicReview extends X402PublicReviewBase {
  version: "boon.x402-review/v2";
  evidenceKind: "receipt_verified";
  recognition: null;
  receipt: {
    routeId: string;
    digest: string;
    resourceUrl: string;
    network: "eip155:8453";
    payer: string;
    payTo: string;
    asset: string;
    amount: string;
    issuedAt: number;
    transaction: string | null;
    serviceSigner: string;
  };
  verification: {
    reviewerSignature: "valid";
    reviewerSignatureKind: "eoa" | "erc1271";
    routedRecognition: null;
    reviewerWasTipper: null;
    receiptPayerBinding: "verified";
    serviceSignerAuthorization: "pinned_at_publication";
    reviewSigningDomain: {
      chainId: number;
      verifyingContract: string;
    };
    publicationCharge: "usd_0.01";
    publicationRail: "x402" | "mpp";
    rankingEligible: false;
  };
  signedPayload?: {
    review: X402Review;
    signature: string;
    evidence: X402ReceiptEvidenceBundle;
  };
}

export interface X402SelfReportedPublicReview extends X402PublicReviewBase {
  version: "boon.x402-review/v3";
  evidenceKind: "self_reported";
  recognition: null;
  receipt: null;
  verification: {
    reviewerSignature: "valid";
    reviewerSignatureKind: "eoa" | "erc1271";
    routedRecognition: null;
    reviewerWasTipper: null;
    receiptPayerBinding: null;
    publicationCharge: "usd_0.05";
    publicationRail: "x402" | "mpp";
    rankingEligible: false;
  };
  signedPayload?: {
    review: X402Review;
    signature: string;
    reviewSigningDomain: { chainId: number; verifyingContract: string };
  };
}

export type X402PublicReview = X402BoonBackedPublicReview | X402ReceiptPublicReview | X402SelfReportedPublicReview;

export interface X402ClientSettlementRecentReview {
  version: "boon.x402-profile-activity-review/v1";
  evidenceKind: "client_settlement";
  routeId: string;
  reviewText: string;
  reviewer: {
    wallet: string;
    accounts: X402LinkedAccount[];
  };
  createdAt: number;
  publishedAt: number;
  recognition: null;
  receipt: null;
  activity: {
    id: string;
    transaction: string;
    network: "eip155:8453";
    asset: string;
    amountAtomic: string;
    occurredAt: number;
  };
  verification: {
    reviewerSignature: "siwx_valid";
    activityOwnerBinding: "verified";
    activeMembershipAtPublication: true;
    rankingEligible: false;
    routedRecognition: null;
    publicationCharge: null;
  };
}

export type X402RecentPublicReview = X402PublicReview | X402ClientSettlementRecentReview;

export function recentReviewLabel(review: X402RecentPublicReview): string {
  if (review.evidenceKind === "boon_backed") return "Boon-backed";
  if (review.evidenceKind === "receipt_verified") return "receipt-verified use";
  if (review.evidenceKind === "client_settlement") return "verified-use-backed";
  return "self-reported";
}

export interface X402RouteRecognition {
  routedBoonCount: number;
  distinctTipperWalletCount: number;
  endpointGratuityUsdc: string;
  networkGratuityUsdc: string;
  totalRecognitionUsdc: string;
  boonBurned: string;
  firstRecognitionAt: string;
  latestRecognitionAt: string;
  latestBlockNumber: string;
  latestTxHash: string;
}

export interface X402RouteDescription {
  version: "boon.x402-route-description/v1";
  routeId: string;
  service: string;
  summary: string;
  route: {
    method: string;
    publicEndpointUrl: string;
  };
  price: {
    display: string;
    amount: string;
    currency: string;
    checkedAt: string;
  };
  receiptAuthority: {
    payTo: string;
    source: "live-x402-challenge";
    signer: string | null;
    signerSource: "live-x402-offer-receipt" | null;
    status: "available" | "not_observed";
    checkedAt: string;
  };
  protocols: string[];
  explorers?: Array<{
    provider: "mppscan" | "x402scan";
    url: string;
  }>;
  request: {
    required: string[];
    notes: string[];
  };
  source: {
    kind: "agentcash-openapi";
    label: string;
    trustTier: string;
    checkedAt: string;
  };
}

export interface X402Route {
  routeId: string;
  description: X402RouteDescription | null;
  endpointUrl: { publicEndpointUrl: string; method: string } | null;
  recognition: X402RouteRecognition;
  reviewSummary: {
    count: number;
    boonBackedCount: number;
    receiptVerifiedCount: number;
    selfReportedCount: number;
    distinctReviewerWalletCount: number;
    latestReviewAt: number | null;
    indexComplete: boolean;
  };
  links: {
    api: string;
    recognitions: string;
    reviews: string;
    web: string;
  };
  contexts: X402RouteContext[];
  reviews: X402PublicReview[];
}

export interface X402GlobalStats {
  routeCount: number;
  routedBoonCount: number;
  endpointGratuityUsdc: string;
  networkGratuityUsdc: string;
  totalRecognitionUsdc: string;
  boonBurned: string;
  latestRecognitionAt: string;
}

export interface X402Interpretation {
  facts: string;
  context: string;
  discovery: string;
  notProofOf: string[];
  sorting: string;
}

export interface X402RoutesResponse {
  version: "1";
  contract: string;
  query: {
    q: string | null;
    origin: string | null;
    method: string | null;
    recognizer: string | null;
    context: X402ContextMode;
    sort: X402SortMode;
    limit: number;
    cursor: string | null;
  };
  navigation: {
    startHere: string;
    routeDirectory: string;
    routeDetailTemplate: string;
    recognitionsTemplate: string;
    reviewsTemplate: string;
    reviewerHistoryTemplate: string;
    pricePerRead: "$0.01";
    web: string;
  };
  routes: X402Route[];
  globalStats: X402GlobalStats | null;
  pagination: {
    limit: number;
    cursor: string | null;
    hasMore: boolean;
    nextCursor: string | null;
  };
  contextIndexComplete: boolean;
  reviewIndexComplete: boolean;
  interpretation: X402Interpretation;
}

export interface X402RouteResponse {
  version: "1";
  contract: string;
  route: X402Route;
  interpretation: X402Interpretation;
  recognitionsUrl: string;
  reviewsUrl: string;
}

export interface X402ReviewsResponse {
  version: "1";
  contract: string;
  routeId: string;
  reviews: X402PublicReview[];
  identityResolution: "complete" | "unavailable";
  pagination: { limit: number; offset: number; total: number; hasMore: boolean; indexComplete: boolean };
}

export interface X402ReviewerReviewItem {
  routeId: string;
  description: X402RouteDescription | null;
  review: X402PublicReview;
}

export interface X402ReviewerReviewsResponse {
  version: "1";
  contract: string;
  reviewer: {
    wallet: string;
    accounts: X402LinkedAccount[];
  };
  items: X402ReviewerReviewItem[];
  identityResolution: "complete" | "unavailable";
  pagination: { limit: number; offset: number; total: number; hasMore: boolean; indexComplete: boolean };
}

export interface X402RecentReviewItem {
  routeId: string;
  profileId: string | null;
  profileAccounts: X402LinkedAccount[];
  route: { origin: string; method: string; path: string } | null;
  description: X402RouteDescription | null;
  review: X402RecentPublicReview;
}

export interface X402RecentReviewsResponse {
  version: "1";
  contract: string;
  items: X402RecentReviewItem[];
  identityResolution: "complete" | "unavailable";
  pagination: { limit: number; hasMore: boolean; indexComplete: boolean };
}

export interface X402SearchInput {
  q?: string;
  origin?: string;
  method?: string;
  recognizer?: string;
  context?: X402ContextMode;
  sort?: X402SortMode;
  limit?: number;
  cursor?: string;
}

export interface X402PublicUsedRoute {
  routeId: string;
  origin: string;
  method: string;
  path: string;
  useCount: number;
  walletCount: number;
  profileCount: number;
  receiptUseCount: number;
  clientUseCount: number;
  reviewCount: number;
  latestUsedAt: number;
}

export async function fetchX402PublicUsedRoutes(limit = 12): Promise<X402PublicUsedRoute[]> {
  const value = await fetchJson(`${X402_SITE_READ_PREFIX}/activity/routes?limit=${Math.min(Math.max(limit, 1), 50)}`);
  if (!isRecord(value) || value.version !== "1" || !Array.isArray(value.routes) ||
      !value.routes.every(isPublicUsedRoute)) {
    throw new Error("The public x402 use index returned malformed data.");
  }
  return value.routes;
}

function isPublicUsedRoute(value: unknown): value is X402PublicUsedRoute {
  return isRecord(value) && isHash(value.routeId) && typeof value.origin === "string" &&
    safeHttpsUrl(value.origin) !== null && typeof value.method === "string" && typeof value.path === "string" &&
    isCount(value.useCount) && isCount(value.walletCount) && isCount(value.profileCount) &&
    isCount(value.receiptUseCount) && isCount(value.clientUseCount) && isCount(value.reviewCount) &&
    isCount(value.latestUsedAt);
}

export function reviewableRecognitions(
  address: string | null | undefined,
  recognitions: RoutedBoonsResponse["routedBoons"] | null,
  reviews: X402PublicReview[],
): RoutedBoonsResponse["routedBoons"] {
  if (!address || !recognitions) return [];
  const normalizedAddress = address.toLowerCase();
  const reviewedEvents = new Set(
    reviews.flatMap((review) => review.evidenceKind === "boon_backed"
      ? [`${review.recognition.transactionHash.toLowerCase()}:${review.recognition.logIndex}`]
      : []),
  );
  return recognitions.filter(
    (row) =>
      row.tipper.toLowerCase() === normalizedAddress &&
      !reviewedEvents.has(`${row.txHash.toLowerCase()}:${row.logIndex}`),
  );
}

export async function fetchX402Routes(input: X402SearchInput = {}): Promise<X402RoutesResponse> {
  const params = new URLSearchParams();
  if (input.q?.trim()) params.set("q", input.q.trim());
  if (input.origin?.trim()) params.set("origin", input.origin.trim());
  if (input.method?.trim()) params.set("method", input.method.trim().toUpperCase());
  if (input.recognizer?.trim()) params.set("recognizer", input.recognizer.trim());
  if (input.context && input.context !== "all") params.set("context", input.context);
  params.set("sort", input.sort ?? "reviewed");
  params.set("limit", String(Math.min(Math.max(input.limit ?? 20, 1), 50)));
  if (input.cursor) params.set("cursor", input.cursor);
  const value = await fetchJson(`${X402_SITE_READ_PREFIX}/routes?${params}`);
  if (!isRoutesResponse(value)) throw new Error("The route index returned malformed data.");
  return value;
}

export interface X402ScanResult {
  version: "1";
  url: string;
  results: Array<{ method: string; status: number | null; x402: boolean; priceUsdc: string | null; network: string | null; payTo?: string | null; asset?: string | null }>;
  recommendedMethod: string | null;
  payTo?: string | null;
  asset?: string | null;
  network?: string | null;
}

export interface X402ServiceEndpoint {
  method: string;
  path: string;
  description: string | null;
  priceUsd: string | null;
}

/** Read a host's standard x402 discovery index (`/.well-known/x402`) so the
 * recognize flow can offer a dropdown of its endpoints. */
export async function fetchX402ServiceEndpoints(host: string): Promise<X402ServiceEndpoint[]> {
  const value = await fetchJson(`${X402_SITE_READ_PREFIX}/service-endpoints?host=${encodeURIComponent(host)}`);
  if (!isRecord(value) || !Array.isArray(value.endpoints)) return [];
  return value.endpoints.filter((e): e is X402ServiceEndpoint => isRecord(e) && typeof e.path === "string" && typeof e.method === "string");
}

/** Probe a public endpoint for an x402 challenge so the review flow can suggest
 * the method and price context instead of asking the human to know them. */
export async function scanX402Endpoint(url: string): Promise<X402ScanResult> {
  const value = await fetchJson(`${X402_SITE_READ_PREFIX}/scan?url=${encodeURIComponent(url)}`);
  if (!isRecord(value) || value.version !== "1" || typeof value.url !== "string" || !Array.isArray(value.results)) {
    throw new Error("The endpoint scan returned malformed data.");
  }
  return value as unknown as X402ScanResult;
}

export async function fetchX402Route(routeId: string): Promise<X402RouteResponse> {
  const value = await fetchJson(`${X402_SITE_READ_PREFIX}/routes/${encodeURIComponent(routeId)}`);
  if (!isRecord(value) || value.version !== "1" || !isAddress(value.contract) || !isX402Route(value.route) ||
      !isInterpretation(value.interpretation) || typeof value.recognitionsUrl !== "string" || typeof value.reviewsUrl !== "string") {
    throw new Error("The route index returned malformed data.");
  }
  return value as unknown as X402RouteResponse;
}

export async function fetchX402Reviews(routeId: string, limit = 100, offset = 0): Promise<X402ReviewsResponse> {
  const value = await fetchJson(
    `${X402_SITE_READ_PREFIX}/routes/${encodeURIComponent(routeId)}/reviews?limit=${Math.min(Math.max(limit, 1), 100)}&offset=${Math.max(offset, 0)}`,
  );
  const reviews = isRecord(value) && Array.isArray(value.reviews)
    ? value.reviews.map((review) => normalizeReviewForDisplay(review, []))
    : null;
  if (!isRecord(value) || value.version !== "1" || !isAddress(value.contract) || value.routeId !== routeId.toLowerCase() ||
      !reviews || reviews.some((review) => review === null) ||
      !(value.identityResolution === "complete" || value.identityResolution === "unavailable") ||
      !isRecord(value.pagination) || !isCount(value.pagination.limit) || !isCount(value.pagination.offset) ||
      !isCount(value.pagination.total) || typeof value.pagination.hasMore !== "boolean" ||
      typeof value.pagination.indexComplete !== "boolean") {
    throw new Error("The review index returned malformed data.");
  }
  return { ...value, reviews } as unknown as X402ReviewsResponse;
}

export async function fetchX402RecentReviews(limit = 3, signal?: AbortSignal): Promise<X402RecentReviewsResponse> {
  const boundedLimit = Math.min(Math.max(limit, 1), 10);
  const value = await fetchRecentReviewsJson(
    `${X402_SITE_READ_PREFIX}/reviews/recent?limit=${boundedLimit}`, signal,
  );
  if (!isRecord(value) || value.version !== "1" || !isAddress(value.contract) || !Array.isArray(value.items) ||
      value.items.length > boundedLimit ||
      !(value.identityResolution === "complete" || value.identityResolution === "unavailable") ||
      !isRecord(value.pagination) || value.pagination.limit !== boundedLimit ||
      typeof value.pagination.hasMore !== "boolean" || typeof value.pagination.indexComplete !== "boolean") {
    throw new Error("The recent review index returned malformed data.");
  }
  const items: X402RecentReviewItem[] = [];
  const seen = new Set<string>();
  let previousPublishedAt = Number.POSITIVE_INFINITY;
  for (const item of value.items) {
    if (!isRecord(item) || !isHash(item.routeId)) throw new Error("The recent review index returned malformed data.");
    const review = normalizeRecentReviewForDisplay(item.review);
    const profileAccounts = normalizeLinkedAccounts(item.profileAccounts);
    const profileId = item.profileId === null ? null : typeof item.profileId === "string" && isProfileId(item.profileId)
      ? item.profileId.toLowerCase() : undefined;
    const route = normalizeRecentRoute(item.route);
    if (!review || review.routeId !== item.routeId || review.evidenceKind === "self_reported" ||
        profileAccounts === null || profileId === undefined ||
        (review.evidenceKind === "client_settlement" && (!profileId || !route)) ||
        review.publishedAt > previousPublishedAt || seen.has(reviewStableKey(review))) {
      throw new Error("The recent review index returned malformed data.");
    }
    previousPublishedAt = review.publishedAt;
    seen.add(reviewStableKey(review));
    const description = item.description !== null && isDescription(item.description, item.routeId)
      ? item.description
      : null;
    items.push({ routeId: item.routeId, profileId, profileAccounts, route, description, review });
  }
  return {
    version: "1",
    contract: value.contract,
    items,
    identityResolution: value.identityResolution,
    pagination: value.pagination as X402RecentReviewsResponse["pagination"],
  };
}

export async function fetchX402ReviewerReviews(
  reviewer: string,
  limit = 10,
  offset = 0,
): Promise<X402ReviewerReviewsResponse> {
  const value = await fetchJson(
    `${X402_SITE_READ_PREFIX}/reviews?reviewer=${encodeURIComponent(reviewer)}&limit=${Math.min(Math.max(limit, 1), 100)}&offset=${Math.max(offset, 0)}`,
  );
  const parsed = parseReviewerReviewsResponse(value);
  if (!parsed) {
    throw new Error("The reviewer history returned malformed data.");
  }
  return parsed;
}

export async function publishX402Review(
  review: X402Review,
  signature: string,
  evidence?: X402ReceiptEvidenceBundle,
): Promise<X402PublicReview> {
  if (!isX402BoonBackedReview(review) || evidence) {
    throw new Error("Paid review publication requires an x402 or MPP paying client. Prepare the signed bundle in the web app, then submit it through AgentCash or the Boon CLI.");
  }
  const value = await fetchJson("/api/v1/x402/reviews", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ review, signature, ...(evidence ? { evidence } : {}) }),
  });
  if (!isRecord(value) || value.version !== "1" || !isReview(value.review)) {
    throw new Error("The review publication returned malformed data.");
  }
  return value.review;
}

/**
 * Record the plaintext endpoint URL for a recognized route so it shows up in
 * discovery without hand-curation. Unsigned and best-effort: the URL is public
 * and self-verifying (the worker only stores it if it recomputes to a
 * recognized route ID). Callers fire this after recognizing or reviewing and
 * ignore failures, since it is additive to the on-chain action that already
 * succeeded.
 */
export async function captureX402EndpointUrl(publicEndpointUrl: string, method: string): Promise<void> {
  await fetchJson(`${X402_SITE_READ_PREFIX}/endpoint-url`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ publicEndpointUrl, method }),
  });
}

export type X402CatalogSource = "coinbase_bazaar" | "circle_marketplace" | "poncho";

export interface X402DiscoveredEndpoint {
  routeId: string;
  url: string;
  method: string;
  label: string;
  service: string | null;
  description: string | null;
  priceUsd: string | null;
  catalogSources?: X402CatalogSource[];
  category?: string | null;
  tags?: string[];
  supportsVanillax402?: boolean | null;
  supportsCircleGateway?: boolean | null;
  siwx?: boolean | null;
  lastUpdated?: string | null;
}

function isDiscoveredEndpoint(endpoint: unknown): endpoint is X402DiscoveredEndpoint {
  return isRecord(endpoint) && typeof endpoint.routeId === "string" && typeof endpoint.url === "string" &&
    typeof endpoint.method === "string" && typeof endpoint.label === "string" &&
    (endpoint.service === null || typeof endpoint.service === "string") &&
    (endpoint.description === null || typeof endpoint.description === "string") &&
    (endpoint.priceUsd === null || typeof endpoint.priceUsd === "string") &&
    (endpoint.catalogSources === undefined || (
      Array.isArray(endpoint.catalogSources) && endpoint.catalogSources.every((source) =>
        source === "coinbase_bazaar" || source === "circle_marketplace" || source === "poncho")
    )) &&
    (endpoint.category === undefined || endpoint.category === null || typeof endpoint.category === "string") &&
    (endpoint.tags === undefined || (Array.isArray(endpoint.tags) && endpoint.tags.every((tag) => typeof tag === "string"))) &&
    (endpoint.supportsVanillax402 === undefined || endpoint.supportsVanillax402 === null || typeof endpoint.supportsVanillax402 === "boolean") &&
    (endpoint.supportsCircleGateway === undefined || endpoint.supportsCircleGateway === null || typeof endpoint.supportsCircleGateway === "boolean") &&
    (endpoint.siwx === undefined || endpoint.siwx === null || typeof endpoint.siwx === "boolean") &&
    (endpoint.lastUpdated === undefined || endpoint.lastUpdated === null || typeof endpoint.lastUpdated === "string");
}

/**
 * Given a wallet, return the x402 endpoints it has recently paid, so a reviewer
 * can pick one instead of typing a URL. Each distinct USDC recipient is resolved
 * against Coinbase's Bazaar by its payTo, so this spans every x402 provider the
 * facilitator has indexed. Matching is provider-level (a payTo is a provider
 * treasury), so results can include sibling endpoints of a provider you paid.
 */
export async function fetchX402WalletEndpoints(wallet: string): Promise<X402DiscoveredEndpoint[]> {
  const value = await fetchJson(`${X402_SITE_READ_PREFIX}/wallet-endpoints?wallet=${encodeURIComponent(wallet)}`);
  if (!isRecord(value) || value.version !== "1" || !Array.isArray(value.endpoints)) {
    throw new Error("The wallet endpoint lookup returned malformed data.");
  }
  return value.endpoints.filter(isDiscoveredEndpoint);
}

/**
 * Task and URL search across live x402 catalogs, so the search box can surface
 * endpoints that are not recognized in Boon yet.
 */
export async function fetchX402Discovered(query: string): Promise<X402DiscoveredEndpoint[]> {
  const value = await fetchJson(`${X402_SITE_READ_PREFIX}/discover?q=${encodeURIComponent(query)}`);
  if (!isRecord(value) || value.version !== "1" || !Array.isArray(value.endpoints)) {
    throw new Error("The x402 discovery search returned malformed data.");
  }
  return value.endpoints.filter(isDiscoveredEndpoint);
}

export function prepareX402ReviewPublication(
  review: X402Review,
  signature: string,
  evidence?: X402ReceiptEvidenceBundle,
): { endpoint: string; price: "$0.05" | "$0.01" | "free"; body: string; agentCashCommand: string } {
  const endpoint = review.version === "3"
    ? "/api/v1/x402/reviews/self-reported"
    : review.version === "2"
      ? "/api/v1/x402/reviews/receipt-verified"
      : "/api/v1/x402/reviews";
  const url = `${API_URL}${endpoint}`;
  const request = { review, signature, ...(evidence ? { evidence } : {}) };
  const compactBody = JSON.stringify(request);
  return {
    endpoint: url,
    price: review.version === "3" ? "$0.05" : review.version === "2" ? "$0.01" : "free",
    body: JSON.stringify(request, null, 2),
    agentCashCommand: `npx agentcash@latest fetch ${url} -m POST -b '${compactBody.replaceAll("'", "'\\''")}'`,
  };
}

export async function fetchX402Recognitions(routeId: string, limit = 100, offset = 0): Promise<RoutedBoonsResponse> {
  const value = await fetchJson(
    `${X402_SITE_READ_PREFIX}/routes/${encodeURIComponent(routeId)}/recognitions?limit=${Math.min(Math.max(limit, 1), 100)}&offset=${Math.max(offset, 0)}`,
  );
  if (!isRecord(value) || value.version !== "1" || !isAddress(value.contract) ||
      !(value.identityResolution === "complete" || value.identityResolution === "unavailable") || !Array.isArray(value.routedBoons) ||
      !value.routedBoons.every(isRoutedBoonRow) || !isRecord(value.interpretation)) {
    throw new Error("The recognition index returned malformed data.");
  }
  return value as unknown as RoutedBoonsResponse;
}

export function safeHttpsEndpoint(context: X402RouteContext): string | null {
  return safeHttpsUrl(context.note.route.publicEndpointUrl);
}

export function safeHttpsUrl(input: string): string | null {
  try {
    const value = new URL(input);
    return value.protocol === "https:" && !value.username && !value.password ? value.href : null;
  } catch {
    return null;
  }
}

export function routeOrigin(context: X402RouteContext | undefined): string | null {
  if (!context) return null;
  try {
    return new URL(context.note.route.publicEndpointUrl).origin;
  } catch {
    return null;
  }
}

class X402ResponseError extends Error {
  constructor(message: string, readonly status: number, readonly retryAfter: string | null) {
    super(message);
  }
}

/** Retry only this free GET, once. Never retry payments, writes, or invalid feed data. */
async function fetchRecentReviewsJson(path: string, signal?: AbortSignal): Promise<unknown> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    signal?.throwIfAborted();
    const timeout = AbortSignal.timeout(8_000);
    try {
      return await fetchJson(path, { signal: signal ? AbortSignal.any([signal, timeout]) : timeout });
    } catch (error) {
      const transient = error instanceof TypeError
        || (error instanceof DOMException && error.name === "TimeoutError")
        || (error instanceof X402ResponseError && [502, 503, 504].includes(error.status) && !error.retryAfter);
      if (attempt > 0 || signal?.aborted || !transient) throw error;
      await new Promise((resolve) => setTimeout(resolve, 350));
    }
  }
  throw new Error("Recent reviews could not be loaded.");
}

async function fetchJson(path: string, init?: RequestInit): Promise<unknown> {
  const headers = new Headers(init?.headers);
  headers.set("accept", "application/json");
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: unknown } | null;
    const detail = body && typeof body.error === "string" ? `: ${body.error}` : "";
    throw new X402ResponseError(`${path} returned ${response.status}${detail}`, response.status, response.headers.get("retry-after"));
  }
  return response.json();
}

function isRoutesResponse(value: unknown): value is X402RoutesResponse {
  if (!isRecord(value) || value.version !== "1" || !isAddress(value.contract) || !Array.isArray(value.routes) ||
      !value.routes.every(isX402Route) || (value.globalStats !== null && !isGlobalStats(value.globalStats)) ||
      !isRecord(value.navigation) || typeof value.navigation.startHere !== "string" ||
      typeof value.navigation.routeDirectory !== "string" ||
      typeof value.navigation.routeDetailTemplate !== "string" ||
      typeof value.navigation.recognitionsTemplate !== "string" ||
      typeof value.navigation.reviewsTemplate !== "string" ||
      typeof value.navigation.reviewerHistoryTemplate !== "string" || typeof value.navigation.web !== "string" ||
      value.navigation.pricePerRead !== "$0.01" ||
      !isRecord(value.pagination) || typeof value.pagination.hasMore !== "boolean" ||
      !(value.pagination.nextCursor === null || typeof value.pagination.nextCursor === "string") ||
      typeof value.contextIndexComplete !== "boolean" || typeof value.reviewIndexComplete !== "boolean" ||
      !isInterpretation(value.interpretation)) return false;
  return true;
}

function isX402Route(value: unknown): value is X402Route {
  if (!isRecord(value) || !isHash(value.routeId) || !isRecognition(value.recognition) ||
      !(value.description === null || isDescription(value.description, value.routeId)) ||
      !isRecord(value.reviewSummary) || !isCount(value.reviewSummary.count) ||
      !isCount(value.reviewSummary.boonBackedCount) || !isCount(value.reviewSummary.receiptVerifiedCount) ||
      !isCount(value.reviewSummary.selfReportedCount) ||
      value.reviewSummary.boonBackedCount + value.reviewSummary.receiptVerifiedCount +
        value.reviewSummary.selfReportedCount !== value.reviewSummary.count ||
      !isCount(value.reviewSummary.distinctReviewerWalletCount) ||
      value.reviewSummary.distinctReviewerWalletCount > value.reviewSummary.count ||
      typeof value.reviewSummary.indexComplete !== "boolean" ||
      !(value.reviewSummary.latestReviewAt === null || isCount(value.reviewSummary.latestReviewAt)) ||
      !isRecord(value.links) || typeof value.links.api !== "string" ||
      typeof value.links.recognitions !== "string" ||
      typeof value.links.reviews !== "string" || typeof value.links.web !== "string" ||
      !Array.isArray(value.contexts) || !value.contexts.every(isContext) ||
      !Array.isArray(value.reviews) || !value.reviews.every(isReview)) return false;
  return true;
}

function parseReviewerReviewsResponse(value: unknown): X402ReviewerReviewsResponse | null {
  if (!isRecord(value) || value.version !== "1" || !isAddress(value.contract) ||
      !isRecord(value.reviewer) || !isAddress(value.reviewer.wallet) ||
      !Array.isArray(value.reviewer.accounts) || !Array.isArray(value.items) ||
      !(value.identityResolution === "complete" || value.identityResolution === "unavailable") ||
      !isRecord(value.pagination) || !isCount(value.pagination.limit) || !isCount(value.pagination.offset) ||
      !isCount(value.pagination.total) || typeof value.pagination.hasMore !== "boolean" ||
      typeof value.pagination.indexComplete !== "boolean") return null;

  const reviewerAccounts = normalizeLinkedAccounts(value.reviewer.accounts);
  if (!reviewerAccounts) return null;
  const reviewerWallet = value.reviewer.wallet;
  const items: X402ReviewerReviewItem[] = [];
  for (const item of value.items) {
    if (!isRecord(item) || !isHash(item.routeId)) return null;
    const review = normalizeReviewForDisplay(item.review, reviewerAccounts);
    if (!review || review.routeId !== item.routeId ||
        review.reviewer.wallet.toLowerCase() !== reviewerWallet.toLowerCase()) return null;
    // Route descriptions are dated discovery context, not review evidence. A
    // stale or partially migrated description must not hide an otherwise valid
    // signed review or its linked reviewer identities.
    const description = item.description !== null && isDescription(item.description, item.routeId)
      ? item.description
      : null;
    items.push({ routeId: item.routeId, description, review });
  }

  return {
    version: "1",
    contract: value.contract,
    reviewer: { wallet: reviewerWallet, accounts: reviewerAccounts },
    items,
    identityResolution: value.identityResolution,
    pagination: value.pagination as X402ReviewerReviewsResponse["pagination"],
  };
}

function normalizeRecentReviewForDisplay(value: unknown): X402RecentPublicReview | null {
  const standard = normalizeReviewForDisplay(value, []);
  if (standard) return standard;
  if (!isRecord(value) || value.version !== "boon.x402-profile-activity-review/v1" ||
      value.evidenceKind !== "client_settlement" || !isHash(value.routeId) ||
      typeof value.reviewText !== "string" || !value.reviewText.trim() ||
      !isRecord(value.reviewer) || !isAddress(value.reviewer.wallet) ||
      !isCount(value.createdAt) || !isCount(value.publishedAt) ||
      !isRecord(value.activity) || !isHash(value.activity.id) || !isHash(value.activity.transaction) ||
      value.activity.network !== "eip155:8453" || !isAddress(value.activity.asset) ||
      !isDecimal(value.activity.amountAtomic) || !isCount(value.activity.occurredAt) ||
      value.recognition !== null || value.receipt !== null || !isRecord(value.verification) ||
      value.verification.reviewerSignature !== "siwx_valid" ||
      value.verification.activityOwnerBinding !== "verified" ||
      value.verification.activeMembershipAtPublication !== true ||
      value.verification.rankingEligible !== false || value.verification.routedRecognition !== null ||
      value.verification.publicationCharge !== null) return null;
  const accounts = normalizeLinkedAccounts(value.reviewer.accounts);
  if (accounts === null) return null;
  return {
    ...value,
    reviewer: { ...value.reviewer, accounts },
  } as unknown as X402ClientSettlementRecentReview;
}

function normalizeRecentRoute(value: unknown): X402RecentReviewItem["route"] {
  if (value === null) return null;
  if (!isRecord(value) || typeof value.origin !== "string" || safeHttpsUrl(value.origin) === null ||
      typeof value.method !== "string" || !/^[A-Z]+$/.test(value.method) ||
      typeof value.path !== "string" || !value.path.startsWith("/") || /[?#]/.test(value.path)) return null;
  return { origin: new URL(value.origin).origin, method: value.method, path: value.path };
}

function isProfileId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function normalizeReviewForDisplay(
  value: unknown,
  fallbackAccounts: X402LinkedAccount[],
): X402PublicReview | null {
  if (!isRecord(value) || !isRecord(value.reviewer) || !isAddress(value.reviewer.wallet)) return null;
  const normalizedAccounts = normalizeLinkedAccounts(value.reviewer.accounts);
  const accounts = normalizedAccounts && normalizedAccounts.length > 0 ? normalizedAccounts : fallbackAccounts;
  const normalized = {
    ...value,
    reviewer: { ...value.reviewer, accounts },
  };
  return isReview(normalized) ? normalized : null;
}

function normalizeLinkedAccounts(value: unknown): X402LinkedAccount[] | null {
  if (!Array.isArray(value)) return null;
  const accounts: X402LinkedAccount[] = [];
  for (const candidate of value) {
    const account = normalizeLinkedAccount(candidate);
    if (account && !accounts.some((current) => current.handle === account.handle)) accounts.push(account);
  }
  return accounts;
}

function normalizeLinkedAccount(value: unknown): X402LinkedAccount | null {
  if (!isRecord(value) || typeof value.handle !== "string" ||
      !(value.provider === "github" || value.provider === "x" || value.provider === "agent") ||
      !isAddress(value.linkedWallet)) return null;
  const expectedVerification = value.provider === "agent" ? "erc8004_indexed_at_recognition" : "boon_oauth_link";
  if (value.verification !== expectedVerification) return null;
  const expectedPrefix = `${value.provider}:`;
  if (!value.handle.startsWith(expectedPrefix) || value.handle.length === expectedPrefix.length) return null;
  const username = value.handle.slice(expectedPrefix.length);
  if (value.provider === "agent" && !/^[1-9][0-9]*$/.test(username)) return null;
  const fallbackProfileUrl = value.provider === "github"
    ? `https://github.com/${encodeURIComponent(username)}`
    : value.provider === "x"
      ? `https://x.com/${encodeURIComponent(username)}`
      : `https://www.8004scan.io/agents/base/${encodeURIComponent(username)}`;
  const profileUrl = typeof value.profileUrl === "string" && safeHttpsUrl(value.profileUrl)
    ? value.profileUrl
    : fallbackProfileUrl;
  const linkedAt = isDecimal(value.linkedAt)
    ? value.linkedAt
    : isCount(value.linkedAt)
      ? String(value.linkedAt)
      : null;
  return {
    handle: value.handle,
    provider: value.provider,
    linkedWallet: value.linkedWallet,
    profileUrl,
    verification: expectedVerification,
    linkedAt,
  };
}

function isReview(value: unknown): value is X402PublicReview {
  if (!isRecord(value) || !isHash(value.routeId) || typeof value.reviewText !== "string" ||
      !isRecord(value.reviewer) || !isAddress(value.reviewer.wallet) || !Array.isArray(value.reviewer.accounts) ||
      !value.reviewer.accounts.every(isLinkedAccount) || !isCount(value.createdAt) || !isCount(value.publishedAt) ||
      !isRecord(value.verification) || value.verification.reviewerSignature !== "valid") return false;
  if (value.version === "boon.x402-review/v2" && value.evidenceKind === "receipt_verified") {
    if (value.recognition !== null || !isRecord(value.receipt) || !isHash(value.receipt.routeId) ||
        !isHash(value.receipt.digest) || typeof value.receipt.resourceUrl !== "string" ||
        safeHttpsUrl(value.receipt.resourceUrl) === null || value.receipt.network !== "eip155:8453" ||
        !isAddress(value.receipt.payer) || !isAddress(value.receipt.payTo) ||
        typeof value.receipt.asset !== "string" || !isDecimal(value.receipt.amount) ||
        !isCount(value.receipt.issuedAt) || !(value.receipt.transaction === null || isHash(value.receipt.transaction)) ||
        !isAddress(value.receipt.serviceSigner) || value.verification.receiptPayerBinding !== "verified" ||
        !(value.verification.reviewerSignatureKind === "eoa" || value.verification.reviewerSignatureKind === "erc1271") ||
        value.verification.serviceSignerAuthorization !== "pinned_at_publication" ||
        !isRecord(value.verification.reviewSigningDomain) ||
        !isCount(value.verification.reviewSigningDomain.chainId) || value.verification.reviewSigningDomain.chainId === 0 ||
        !isAddress(value.verification.reviewSigningDomain.verifyingContract) ||
        value.verification.publicationCharge !== "usd_0.01" ||
        !(value.verification.publicationRail === "x402" || value.verification.publicationRail === "mpp") ||
        value.verification.rankingEligible !== false) return false;
    return value.reviewer.wallet.toLowerCase() === value.receipt.payer.toLowerCase();
  }
  if (value.version === "boon.x402-review/v3" && value.evidenceKind === "self_reported") {
    return value.recognition === null && value.receipt === null &&
      (value.verification.reviewerSignatureKind === "eoa" || value.verification.reviewerSignatureKind === "erc1271") &&
      value.verification.receiptPayerBinding === null && value.verification.rankingEligible === false &&
      value.verification.publicationCharge === "usd_0.05" &&
      (value.verification.publicationRail === "x402" || value.verification.publicationRail === "mpp");
  }
  if (value.version !== "boon.x402-review/v1" || value.evidenceKind !== "boon_backed" ||
      !isRecord(value.recognition) || value.receipt !== null) return false;
  const recognition = value.recognition;
  if (!isHash(recognition.routeId) || !isHash(recognition.transactionHash) ||
      !["logIndex", "endpointGratuityUsdc", "networkGratuityUsdc", "totalRecognitionUsdc", "boonBurned", "blockTimestamp"]
        .every((field) => isDecimal(recognition[field])) ||
      value.verification.reviewerSignature !== "valid" || value.verification.routedRecognition !== "verified" ||
      value.verification.reviewerWasTipper !== true) return false;
  if (value.signedPayload !== undefined) {
    if (!isRecord(value.signedPayload) || !isRecord(value.signedPayload.review) ||
        typeof value.signedPayload.signature !== "string" || !/^0x[0-9a-fA-F]{130}$/.test(value.signedPayload.signature) ||
        value.signedPayload.review.reviewText !== value.reviewText ||
        value.signedPayload.review.routeId !== recognition.routeId ||
        value.signedPayload.review.recognitionTxHash !== recognition.transactionHash) return false;
  }
  try {
    return BigInt(recognition.totalRecognitionUsdc as string) ===
      BigInt(recognition.endpointGratuityUsdc as string) + BigInt(recognition.networkGratuityUsdc as string) &&
      BigInt(recognition.boonBurned as string) === 100_000n * 10n ** 18n;
  } catch {
    return false;
  }
}

export function reviewRouteId(review: X402PublicReview): string {
  return review.routeId;
}

export function reviewStableKey(review: X402RecentPublicReview): string {
  return review.evidenceKind === "boon_backed"
    ? `${review.recognition.transactionHash}:${review.recognition.logIndex}`
    : review.evidenceKind === "receipt_verified"
      ? review.receipt.digest
      : review.evidenceKind === "client_settlement"
        ? review.activity.id
        : `${review.routeId}:${review.reviewer.wallet.toLowerCase()}`;
}

function isDescription(value: unknown, routeId: unknown): value is X402RouteDescription {
  if (!isRecord(value) || value.version !== "boon.x402-route-description/v1" || value.routeId !== routeId ||
      typeof value.service !== "string" || typeof value.summary !== "string" ||
      !isRecord(value.route) || typeof value.route.method !== "string" ||
      safeHttpsUrl(String(value.route.publicEndpointUrl ?? "")) === null ||
      !isRecord(value.price) || typeof value.price.display !== "string" ||
      typeof value.price.amount !== "string" || typeof value.price.currency !== "string" ||
      typeof value.price.checkedAt !== "string" || !isRecord(value.receiptAuthority) ||
      !isAddress(value.receiptAuthority.payTo) || value.receiptAuthority.source !== "live-x402-challenge" ||
      !(value.receiptAuthority.status === "available" || value.receiptAuthority.status === "not_observed") ||
      !((value.receiptAuthority.status === "available" && isAddress(value.receiptAuthority.signer) &&
          value.receiptAuthority.signerSource === "live-x402-offer-receipt") ||
        (value.receiptAuthority.status === "not_observed" && value.receiptAuthority.signer === null &&
          value.receiptAuthority.signerSource === null)) ||
      typeof value.receiptAuthority.checkedAt !== "string" || !Array.isArray(value.protocols) ||
      !value.protocols.every((item) => typeof item === "string") || !isRecord(value.request) ||
      !Array.isArray(value.request.required) || !value.request.required.every((item) => typeof item === "string") ||
      !Array.isArray(value.request.notes) || !value.request.notes.every((item) => typeof item === "string") ||
      !isRecord(value.source) || value.source.kind !== "agentcash-openapi" ||
      typeof value.source.label !== "string" || typeof value.source.trustTier !== "string" ||
      typeof value.source.checkedAt !== "string") return false;
  if (value.explorers !== undefined &&
      (!Array.isArray(value.explorers) || !value.explorers.every(isExplorerLink))) return false;
  return true;
}

function isExplorerLink(value: unknown): value is NonNullable<X402RouteDescription["explorers"]>[number] {
  if (!isRecord(value) || !(value.provider === "mppscan" || value.provider === "x402scan") ||
      typeof value.url !== "string" || safeHttpsUrl(value.url) === null) return false;
  try {
    const url = new URL(value.url);
    const expectedHost = value.provider === "mppscan" ? "mppscan.com" : "www.x402scan.com";
    return url.hostname === expectedHost && /^\/server\/[A-Za-z0-9-]+$/.test(url.pathname) &&
      !url.search && !url.hash;
  } catch {
    return false;
  }
}

function isRecognition(value: unknown): value is X402RouteRecognition {
  if (!isRecord(value) || !isCount(value.routedBoonCount) || !isCount(value.distinctTipperWalletCount) ||
      value.distinctTipperWalletCount > value.routedBoonCount || !isHash(value.latestTxHash)) return false;
  if (!["endpointGratuityUsdc", "networkGratuityUsdc", "totalRecognitionUsdc", "boonBurned", "firstRecognitionAt", "latestRecognitionAt", "latestBlockNumber"]
    .every((field) => isDecimal(value[field]))) return false;
  try {
    return BigInt(value.totalRecognitionUsdc as string) ===
        BigInt(value.endpointGratuityUsdc as string) + BigInt(value.networkGratuityUsdc as string) &&
      BigInt(value.boonBurned as string) === 100_000n * 10n ** 18n * BigInt(value.routedBoonCount);
  } catch {
    return false;
  }
}

function isContext(value: unknown): value is X402RouteContext {
  if (!isRecord(value) || value.version !== "boon.x402-route-context/v1" || !isAddress(value.publisher) ||
      !Array.isArray(value.participantRoles) || !value.participantRoles.every((role) => role === "tipper" || role === "endpoint" || role === "network") ||
      !isCount(value.publishedAt) || !Array.isArray(value.publisherAccounts) ||
      !value.publisherAccounts.every(isLinkedAccount) || !isRecord(value.note) || !isRecord(value.note.route) ||
      typeof value.note.route.publicEndpointUrl !== "string" || typeof value.note.route.method !== "string" ||
      typeof value.note.intent !== "string" || typeof value.note.fit !== "string" || !Array.isArray(value.note.limits) ||
      !value.note.limits.every((item) => typeof item === "string") || !isRecord(value.offer) || !isRecord(value.verification)) return false;
  if (value.signedPayload !== undefined &&
      (!isRecord(value.signedPayload) || typeof value.signedPayload.noteJson !== "string" ||
       typeof value.signedPayload.offerJson !== "string" ||
       typeof value.signedPayload.signature !== "string" ||
       !/^0x(?:[0-9a-fA-F]{128}|[0-9a-fA-F]{130})$/.test(value.signedPayload.signature))) return false;
  return value.verification.publisherSignature === "valid" && value.verification.exactNoteBytes === "valid" &&
    value.verification.endpointFetched === false;
}

function isLinkedAccount(value: unknown): value is X402LinkedAccount {
  return isRecord(value) && typeof value.handle === "string" &&
    (value.provider === "github" || value.provider === "x" || value.provider === "agent") && isAddress(value.linkedWallet) &&
    safeHttpsUrl(String(value.profileUrl ?? "")) !== null &&
    (value.verification === "boon_oauth_link" || value.verification === "erc8004_indexed_at_recognition") &&
    (value.linkedAt === undefined || value.linkedAt === null || isDecimal(value.linkedAt));
}

function isGlobalStats(value: unknown): value is X402GlobalStats {
  if (!isRecord(value) || !isCount(value.routeCount) || !isCount(value.routedBoonCount)) return false;
  return ["endpointGratuityUsdc", "networkGratuityUsdc", "totalRecognitionUsdc", "boonBurned", "latestRecognitionAt"]
    .every((field) => isDecimal(value[field]));
}

function isInterpretation(value: unknown): value is X402Interpretation {
  return isRecord(value) && typeof value.facts === "string" && typeof value.context === "string" &&
    typeof value.discovery === "string" && typeof value.sorting === "string" &&
    Array.isArray(value.notProofOf) && value.notProofOf.every((item) => typeof item === "string");
}

function isRoutedBoonRow(value: unknown): boolean {
  if (!isRecord(value) || !isHash(value.routeId) || !isHash(value.txHash) || !isAddress(value.tipper) ||
      !isAddress(value.endpointPayTo) || !isAddress(value.networkPayTo)) return false;
  if (!isRecord(value.accounts) || !Array.isArray(value.accounts.recognizer) ||
      !Array.isArray(value.accounts.endpoint) || !Array.isArray(value.accounts.network) ||
      ![...value.accounts.recognizer, ...value.accounts.endpoint, ...value.accounts.network].every(isLinkedAccount)) return false;
  return ["endpointGratuityUsdc", "networkGratuityUsdc", "boonBurned", "blockNumber", "blockTimestamp", "logIndex"]
    .every((field) => isDecimal(value[field]));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
function isDecimal(value: unknown): value is string {
  return typeof value === "string" && value.length <= 78 && /^(?:0|[1-9][0-9]*)$/.test(value);
}
function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function isAddress(value: unknown): value is string {
  return typeof value === "string" && /^0x[0-9a-fA-F]{40}$/.test(value);
}
function isHash(value: unknown): value is string {
  return typeof value === "string" && /^0x[0-9a-fA-F]{64}$/.test(value);
}
